<template>
  <div class="w-full">
    <Head title="Kategorier" />
    <!-- Hero -->
    <section class="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-16">
      <div class="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
        <!-- Left: text -->
        <div class="flex-1 flex flex-col gap-5 md:gap-6">
          <h1
            class="font-[Poppins] font-bold text-[#191c1e] tracking-[-2px] leading-[1.2]"
            style="font-size: clamp(36px, 5vw, 60px)"
          >
            Visa recept smaker<br />
            <span>efter </span>
            <span class="text-teal-500">kategori</span>
          </h1>
          <p class="text-[18px] text-[#3c4947] leading-[30px] max-w-[576px]">
            Här har jag kategoriserat alla mina recept. Oavsett om du är sugen
            på något snabbt efter jobbet eller vill spendera hela lördagen med
            att baka, så finns inspirationen här under.
          </p>
          <Link
            v-if="adminCategoriesPath"
            :href="adminCategoriesPath"
            class="self-start text-[13px] font-semibold text-teal-600 border border-teal-200 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg transition-colors"
            >Hantera kategorier</Link
          >
        </div>

        <!-- Right: hero image -->
        <div class="flex-1 w-full hidden md:block">
          <div
            class="overflow-hidden rounded-3xl shadow-2xl"
            style="height: clamp(220px, 40vw, 400px)"
          >
            <img
              v-if="pageConfig.hero_image_url"
              :src="pageConfig.hero_image_url"
              alt="Kategorier"
              class="w-full h-full object-cover"
            />
            <div
              v-else
              class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Featured grid: first 6 categories -->
    <section
      v-if="featuredCategories.length > 0"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-12 md:pb-16"
    >
      <!-- Row 1: large left card + right column -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-stretch">
        <!-- Large card: index 0 -->
        <Link
          :href="featuredCategories[0].path"
          class="relative overflow-hidden rounded-3xl block h-[clamp(280px,50vw,640px)] md:h-auto md:min-h-full"
          style="text-decoration: none"
        >
          <img
            v-if="featuredCategories[0].cover_image_url"
            :src="featuredCategories[0].cover_image_url"
            :alt="featuredCategories[0].name"
            class="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div v-else class="w-full h-full bg-teal-100" />
          <div
            class="absolute inset-0 flex flex-col justify-end p-6 md:p-8"
            style="
              background: linear-gradient(
                to top,
                rgba(0, 0, 0, 0.75) 0%,
                rgba(0, 0, 0, 0.1) 60%,
                rgba(0, 0, 0, 0) 100%
              );
            "
          >
            <div>
              <h3
                class="font-[Poppins] font-bold text-white mb-2"
                style="font-size: clamp(22px, 3vw, 30px)"
              >
                {{ featuredCategories[0].name }}
              </h3>
              <p class="text-[15px] text-white/85 max-w-[340px] mb-3">
                {{
                  featuredCategories[0].subtitle ||
                  `Alla recept inom ${featuredCategories[0].name.toLowerCase()} samlade på ett ställe.`
                }}
              </p>
              <span
                class="inline-flex items-center gap-2 text-teal-400 font-bold text-sm"
              >
                Visa recept <ArrowRight :size="14" />
              </span>
            </div>
          </div>
        </Link>

        <!-- Right column: medium + 2 small -->
        <div class="flex flex-col gap-4 md:gap-6">
          <!-- Medium card: index 1 -->
          <Link
            v-if="featuredCategories[1]"
            :href="featuredCategories[1].path"
            class="relative overflow-hidden rounded-3xl block"
            style="height: clamp(200px, 30vw, 300px); text-decoration: none"
          >
            <img
              v-if="featuredCategories[1].cover_image_url"
              :src="featuredCategories[1].cover_image_url"
              :alt="featuredCategories[1].name"
              class="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div v-else class="w-full h-full bg-teal-100" />
            <div
              class="absolute inset-0 flex flex-col justify-end p-5 md:p-6"
              style="
                background: linear-gradient(
                  to top,
                  rgba(0, 0, 0, 0.7) 0%,
                  rgba(0, 0, 0, 0) 60%
                );
              "
            >
              <div>
                <h3
                  class="font-[Poppins] font-bold text-white mb-1"
                  style="font-size: clamp(18px, 2.5vw, 24px)"
                >
                  {{ featuredCategories[1].name }}
                </h3>
                <p class="text-[13px] text-white/85 mb-2">
                  {{
                    featuredCategories[1].subtitle ||
                    `Alla recept inom ${featuredCategories[1].name.toLowerCase()} samlade på ett ställe.`
                  }}
                </p>
                <span
                  class="inline-flex items-center gap-2 text-teal-400 font-bold text-sm"
                >
                  Visa recept <ArrowRight :size="14" />
                </span>
              </div>
            </div>
          </Link>

          <!-- Two small cards: index 2 + 3 -->
          <div class="grid grid-cols-2 gap-4 md:gap-6">
            <Link
              v-for="cat in featuredCategories.slice(2, 4)"
              :key="cat.id"
              :href="cat.path"
              class="relative overflow-hidden rounded-3xl block"
              style="height: clamp(180px, 25vw, 310px); text-decoration: none"
            >
              <img
                v-if="cat.cover_image_url"
                :src="cat.cover_image_url"
                :alt="cat.name"
                class="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div v-else class="w-full h-full bg-teal-100" />
              <div
                class="absolute inset-0 flex flex-col justify-end p-4 md:p-5"
                style="
                  background: linear-gradient(
                    to top,
                    rgba(0, 0, 0, 0.7) 0%,
                    rgba(0, 0, 0, 0) 50%
                  );
                "
              >
                <div>
                  <h3
                    class="font-[Poppins] font-bold text-white mb-1"
                    style="font-size: clamp(14px, 2vw, 20px)"
                  >
                    {{ cat.name }}
                  </h3>
                  <p class="text-[14px] text-white/75 mb-2">
                    {{
                      cat.subtitle ||
                      `Alla recept inom ${cat.name.toLowerCase()} samlade på ett ställe.`
                    }}
                  </p>
                  <span
                    class="inline-flex items-center gap-2 text-teal-400 font-bold text-[14px]"
                  >
                    Visa recept <ArrowRight :size="14" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <!-- Row 2: horizontal cards for index 4 + 5 -->
      <div
        v-if="featuredCategories.length > 4"
        class="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6"
      >
        <Link
          v-for="cat in featuredCategories.slice(4, 6)"
          :key="cat.id"
          :href="cat.path"
          class="flex items-center gap-4 p-4 rounded-2xl border bg-white hover:shadow-md transition-shadow"
          style="border-color: #eceef0; text-decoration: none"
        >
          <div
            class="overflow-hidden rounded-xl shrink-0"
            style="width: 90px; height: 90px"
          >
            <img
              v-if="cat.cover_image_url"
              :src="cat.cover_image_url"
              :alt="cat.name"
              class="w-full h-full object-cover"
              loading="lazy"
            />
            <div v-else class="w-full h-full bg-teal-100" />
          </div>
          <div class="flex-1">
            <h4
              class="font-[Poppins] font-bold text-[18px] text-[#191c1e] mb-1"
            >
              {{ cat.name }}
            </h4>
            <p class="text-[14px] text-[#6c7a77]">
              {{
                cat.subtitle ||
                `Alla recept inom ${cat.name.toLowerCase()} samlade på ett ställe.`
              }}
            </p>
          </div>
          <ArrowRight :size="18" color="#14b8a6" />
        </Link>
      </div>
    </section>

    <!-- Extra categories: index 6+ -->
    <section
      v-if="extraCategories.length > 0"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-12 md:pb-16"
    >
      <h2 class="font-[Poppins] font-bold text-[#191c1e] text-[22px] mb-5">
        Fler kategorier
      </h2>

      <!-- Top 3: image cards in a row -->
      <div
        v-if="extraTopThree.length > 0"
        class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6"
      >
        <Link
          v-for="cat in extraTopThree"
          :key="cat.id"
          :href="cat.path"
          class="relative overflow-hidden rounded-3xl block"
          style="height: clamp(180px, 25vw, 310px); text-decoration: none"
        >
          <img
            v-if="cat.cover_image_url"
            :src="cat.cover_image_url"
            :alt="cat.name"
            class="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div v-else class="w-full h-full bg-teal-100" />
          <div
            class="absolute inset-0 flex flex-col justify-end p-4 md:p-5"
            style="
              background: linear-gradient(
                to top,
                rgba(0, 0, 0, 0.7) 0%,
                rgba(0, 0, 0, 0) 50%
              );
            "
          >
            <div>
              <h3
                class="font-[Poppins] font-bold text-white mb-1"
                style="font-size: clamp(14px, 2vw, 20px)"
              >
                {{ cat.name }}
              </h3>
              <p class="text-[14px] text-white/90 mb-2 line-clamp-2">
                {{
                  cat.subtitle ||
                  `Alla recept inom ${cat.name.toLowerCase()} samlade på ett ställe.`
                }}
              </p>
              <span
                class="inline-flex items-center gap-2 text-teal-400 font-bold text-[14px]"
              >
                Visa recept <ArrowRight :size="14" />
              </span>
            </div>
          </div>
        </Link>
      </div>

      <!-- Remaining: list cards -->
      <div
        v-if="extraRest.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 gap-3"
      >
        <Link
          v-for="cat in extraRest"
          :key="cat.id"
          :href="cat.path"
          class="flex items-center gap-4 p-4 rounded-2xl border bg-white hover:shadow-md transition-shadow"
          style="border-color: #eceef0; text-decoration: none"
        >
          <div
            class="overflow-hidden rounded-xl shrink-0"
            style="width: 72px; height: 72px"
          >
            <img
              v-if="cat.cover_image_url"
              :src="cat.cover_image_url"
              :alt="cat.name"
              class="w-full h-full object-cover"
            />
            <div v-else class="w-full h-full bg-teal-100 rounded-xl" />
          </div>
          <div class="flex-1">
            <h4
              class="font-[Poppins] font-bold text-[16px] text-[#191c1e] mb-0.5"
            >
              {{ cat.name }}
            </h4>
            <p class="text-[13px] text-[#6c7a77]">
              {{
                cat.subtitle ||
                `Alla recept inom ${cat.name.toLowerCase()} samlade på ett ställe.`
              }}
            </p>
          </div>
          <ArrowRight :size="18" color="#14b8a6" />
        </Link>
      </div>
    </section>

    <!-- Instagram banner -->
    <section class="max-w-[1280px] mx-auto px-4 md:px-6 pb-16 md:pb-20">
      <div
        class="overflow-hidden rounded-[28px] md:rounded-[40px] flex flex-col md:flex-row md:items-center"
        :style="{
          backgroundColor: pageConfig.banner_image_url ? '#f0fdf9' : '#f0fdf9',
        }"
      >
        <!-- Text content -->
        <div class="relative z-10 p-8 md:p-12 md:w-1/2">
          <h2
            class="font-[Poppins] font-extrabold text-[#191c1e] leading-[1.2] mb-3"
            style="font-size: clamp(18px, 4vw, 36px)"
          >
            Följ Emmalagat på Instagram
          </h2>
          <p
            class="hidden sm:block text-[16px] text-[#3c4947] leading-[26px] mb-6"
          >
            Få inspiration direkt i ditt flöde. Inget krångel, bara ren
            matsglädje.
          </p>
          <a
            href="https://www.instagram.com/emmalagat.se/"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 px-4 py-2 md:px-8 md:py-4 rounded-xl bg-teal-500 hover:bg-teal-600 transition-colors"
            style="text-decoration: none"
          >
            <span class="font-bold text-[13px] md:text-[15px] text-white"
              >Gå till Instagram</span
            >
          </a>
        </div>

        <!-- Banner image -->
        <div class="md:w-1/2 h-48 md:h-72">
          <template v-if="pageConfig.banner_image_url">
            <img
              :src="pageConfig.banner_image_url"
              alt="Instagram"
              class="w-full h-full object-cover"
              loading="lazy"
            />
          </template>
          <div
            v-else
            class="w-full h-full opacity-20"
            style="
              background: radial-gradient(
                circle at 70% 50%,
                #14b8a6 0%,
                transparent 70%
              );
            "
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { Head, Link } from "@inertiajs/vue3";
import { ArrowRight } from "lucide-vue-next";

const props = defineProps({
  categories: { type: Array, default: () => [] },
  pageConfig: { type: Object, default: () => ({}) },
  currentUser: { type: Object, default: null },
  adminCategoriesPath: { type: String, default: null },
});

// Positions 1-6: featured grid
const featuredCategories = computed(() =>
  props.categories.filter((c) => c.position >= 1 && c.position <= 6),
);
// Positions 7-9: "Fler kategorier" image row
const extraTopThree = computed(() =>
  props.categories.filter((c) => c.position >= 7 && c.position <= 9),
);
// No position: list cards
const extraRest = computed(() => props.categories.filter((c) => !c.position));
// All extra categories combined (for section visibility)
const extraCategories = computed(() => [
  ...extraTopThree.value,
  ...extraRest.value,
]);
</script>
